import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { GraphQLOperationName } from '@api-ui-app/src/app/service/graphql-interceptor.types';

@Injectable()
export class GraphQLSentryLoggerInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const startedAt = Date.now();
    const uniqueRequestId = self.crypto?.randomUUID(); // Generate a unique ID for the request

    // Modify the request to include the unique ID as a header
    const modifiedReq = req.clone({
      headers: req.headers.set('X-Request-ID', uniqueRequestId),
    });

    // Extract and log GraphQL operation details at the beginning of the request
    let operationName: GraphQLOperationName = GraphQLOperationName.UnknownOperation;
    let variables = {};

    try {
      const body = req.body;
      variables = body.variables;
      operationName = req.body?.variables?.operationName as GraphQLOperationName;

      if (this.isTrackedOperation(operationName)) {
        this.logRequestStart(req, operationName, variables, uniqueRequestId);
      }
    } catch (error) {
      if (this.isTrackedOperation(operationName)) {
        this.logErrorParsing(req, operationName, error, variables);
      }
    }

    return next.handle(modifiedReq).pipe(
      tap(event => {
        if (event instanceof HttpResponse && this.isTrackedOperation(operationName)) {
          this.logResponse(req, operationName, uniqueRequestId, event, startedAt);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.isTrackedOperation(operationName)) {
          this.logResponseError(req, operationName, variables, error);
        }
        return throwError(error);
      }),
    );
  }

  private isTrackedOperation(operationName: GraphQLOperationName): boolean {
    return [
      GraphQLOperationName.PRODUCT_VARIANT_COST_V2,
      GraphQLOperationName.FETCH_CURRENCY_RATE,
      GraphQLOperationName.FETCH_PRICE_LIST,
    ].includes(operationName);
  }

  private logRequestStart(req: HttpRequest<any>, operationName: string, variables: any, uniqueRequestId: string): void {
    Sentry.addBreadcrumb({
      category: 'graphql',
      message: `GraphQL request started for ${operationName}`,
      data: {
        url: req.url,
        method: req.method,
        operationName,
        variables,
        uniqueRequestId,
      },
      level: Sentry.Severity.Info,
    });
  }

  private logErrorParsing(req: HttpRequest<any>, operationName: string, error: any, variables: any): void {
    Sentry.captureException(error, {
      extra: {
        url: req.url,
        method: req.method,
        operationName,
        variables,
        errorMessage: `Failed to parse GraphQL request details for ${operationName}`,
      },
    });
  }

  private logResponse(
    req: HttpRequest<any>,
    operationName: string,
    uniqueRequestId: string,
    event: HttpResponse<any>,
    startedAt: number,
  ): void {
    const elapsedMs = Date.now() - startedAt;
    Sentry.addBreadcrumb({
      category: 'graphql',
      message: `GraphQL response received for ${operationName}`,
      data: {
        url: req.url,
        method: req.method,
        operationName,
        status: event.status,
        responseTime: `${elapsedMs} ms`,
        'response-request-id': event.headers.get('Request-ID') || 'N/A',
      },
      level: Sentry.Severity.Info,
    });
  }

  private logResponseError(req: HttpRequest<any>, operationName: string, variables, error: HttpErrorResponse): void {
    Sentry.captureException(error, {
      extra: {
        url: req.url,
        method: req.method,
        operationName,
        variables,
        errorMessage: `Error received in response for ${operationName}`,
      },
    });
  }
}
